import { StringData } from "../../core/StringData";
import { StringObject } from "../../objects/StringObject";
import { frameDOM } from "../../utils/frame-dom";
import { styleTxn } from "../../utils/style-txn";
import { CursorReactiveModule } from "./CursorReactiveModule";

type SpotlightComputeKernel = {
  computeStep(
    pointerDx: number,
    pointerDy: number,
    pointerDist: number,
    deadzone: number,
    distMax: number,
    lerp: number,
    currentAngle: number,
    currentDist: number,
    currentTargetAngle: number,
    prevDeg: number,
    prevDist: number,
    angleEps: number,
    distEps: number,
    forceImmediate: number,
    forceEmit: number,
    bypassDeadzone: number,
    out: SpotlightComputeStepResult
  ): void;
};

type SpotlightComputeStepResult = {
  angle: number;
  dist: number;
  tAngle: number;
  tDist: number;
  degRaw: number;
  cssDeg: number;
  cssDist: number;
  angleChanged: number;
  distChanged: number;
};

const TAU = Math.PI * 2;
const RAD2DEG = 180 / Math.PI;
const clamp01 = (v: number) => (v < 0 ? 0 : v > 1 ? 1 : v);

const shortestArc = (from: number, to: number) => {
  let d = (to - from) % TAU;
  if (d > Math.PI) d -= TAU;
  if (d < -Math.PI) d += TAU;
  return from + d;
};

const DEFAULT_SPOTLIGHT_KERNEL: SpotlightComputeKernel = {
  computeStep(
    pointerDx: number,
    pointerDy: number,
    pointerDist: number,
    deadzone: number,
    distMax: number,
    lerp: number,
    currentAngle: number,
    currentDist: number,
    currentTargetAngle: number,
    prevDeg: number,
    prevDist: number,
    angleEps: number,
    distEps: number,
    forceImmediate: number,
    forceEmit: number,
    bypassDeadzone: number,
    out: SpotlightComputeStepResult
  ): void {
    let targetAngle = currentTargetAngle;
    if (bypassDeadzone === 1 || pointerDist > deadzone) {
      targetAngle = Math.atan2(pointerDy, pointerDx);
    }

    const targetDist = distMax > 0 ? Math.min(pointerDist, distMax) : pointerDist;

    let angle = currentAngle;
    let dist = currentDist;
    if (forceImmediate === 1) {
      angle = targetAngle;
      dist = targetDist;
    } else {
      const nextA = shortestArc(currentAngle, targetAngle);
      const factor = clamp01(lerp);
      angle += (nextA - angle) * factor;
      dist += (targetDist - dist) * factor;
    }

    const degRaw = angle * RAD2DEG - 90;
    const angleChanged = forceEmit === 1 || Number.isNaN(prevDeg) || Math.abs(degRaw - prevDeg) > angleEps;
    const distChanged = forceEmit === 1 || Number.isNaN(prevDist) || Math.abs(dist - prevDist) > distEps;

    out.angle = angle;
    out.dist = dist;
    out.tAngle = targetAngle;
    out.tDist = targetDist;
    out.degRaw = degRaw;
    out.cssDeg = Math.round(degRaw * 10) / 10;
    out.cssDist = Math.round(dist * 10) / 10;
    out.angleChanged = angleChanged ? 1 : 0;
    out.distChanged = distChanged ? 1 : 0;
  },
};

type SpotlightState = {
  angle: number;
  dist: number;
  tAngle: number;
  tDist: number;
  prevDeg: number;
  prevDist: number;
};

const SCROLL_UPDATE_OPTIONS = { bypassDeadzone: true } as const;
const INITIALIZE_OPTIONS = {
  forceImmediate: true,
  forceEmit: true,
  bypassDeadzone: true,
} as const;

const spotState = new WeakMap<StringObject, SpotlightState>();
const getSpot = (o: StringObject): SpotlightState => {
  let s = spotState.get(o);
  if (!s) {
    s = { angle: 0, dist: 0, tAngle: 0, tDist: 0, prevDeg: NaN, prevDist: NaN };
    spotState.set(o, s);
  }
  return s;
};

export class StringSpotlight extends CursorReactiveModule {
  private readonly stepResult: SpotlightComputeStepResult = {
    angle: 0,
    dist: 0,
    tAngle: 0,
    tDist: 0,
    degRaw: 0,
    cssDeg: 0,
    cssDist: 0,
    angleChanged: 0,
    distChanged: 0,
  };

  constructor(context: any) {
    super(context);
    this.htmlKey = "spotlight";
    this.cssProperties = [
      { name: "--spotlight-angle", syntax: "<number>", initialValue: "0", inherits: true },
      { name: "--spotlight-distance", syntax: "<number>", initialValue: "0", inherits: true },
    ];

    this.nearOnly = false;
    this.useAllObjects = false;
    this.maxDistanceMultiplier = 1;

    this.attributesToMap.push(
      {
        key: "lerp",
        type: "number",
        fallback: this.settings["lerp"],
        transform: (v: number) =>
          clamp01(
            this.tools.adaptiveLerp.process({
              value: v,
              inMin: 0.1,
              inMax: 1.0,
              outMin: 0.05,
              outMax: 0.65,
            })
          ),
      },
      { key: "angle-threshold", type: "number", fallback: 0.2 },
      { key: "distance-threshold", type: "number", fallback: 0.5 },
      { key: "deadzone", type: "number", fallback: 4 },
      { key: "dist-max", type: "number", fallback: 0 }
    );
  }

  initializeObject(
    id: number,
    obj: StringObject,
    el: HTMLElement,
    attrs: Record<string, any>
  ): void {
    super.initializeObject(id, obj, el, attrs);

    obj.setProperty("spotlight-angle-rad", 0);
    obj.setProperty("spotlight-distance", 0);
    obj.setProperty("spotlight-angle-rad-target", 0);
    obj.setProperty("spotlight-distance-target", 0);

    frameDOM.measure(() => {
      this.refreshPointerState(obj, /*allowFallback=*/ true);
      frameDOM.mutate(() => {
        styleTxn.run(() => {
          this.updateSpotlightState(obj, INITIALIZE_OPTIONS);
        });
      });
    });
  }

  override onMutate(data: StringData): void {
    if (this.enabled) {
      super.onMutate(data);
      const targets = this.getCursorTargets(/*allowFallback=*/ false);
      const list = targets.length > 0 ? targets : this.getCursorTargets(/*allowFallback=*/ true);
      for (let i = 0; i < list.length; i++) {
        const object = list[i];
        this.updateSpotlightState(object);
      }
    }
  }

  protected onCursorScrollUpdate(): void {
    const targets = this.getCursorTargets(/*allowFallback=*/ false);
    const list = targets.length > 0 ? targets : this.getCursorTargets(/*allowFallback=*/ true);

    for (let i = 0; i < list.length; i++) {
      const object = list[i];
      this.updateSpotlightState(object, SCROLL_UPDATE_OPTIONS);
    }
  }

  private updateSpotlightState(
    object: StringObject,
    options: { forceImmediate?: boolean; forceEmit?: boolean; bypassDeadzone?: boolean } = {}
  ): void {
    const dx = object.getProperty<number>("dx");
    const dy = object.getProperty<number>("dy");
    const dist = object.getProperty<number>("dist");

    if (!Number.isFinite(dx) || !Number.isFinite(dy) || !Number.isFinite(dist)) {
      return;
    }

    const s = getSpot(object);
    DEFAULT_SPOTLIGHT_KERNEL.computeStep(
      dx,
      dy,
      dist,
      object.getProperty<number>("deadzone") ?? 4,
      object.getProperty<number>("dist-max") ?? 0,
      object.getProperty<number>("lerp") ?? 0.15,
      s.angle,
      s.dist,
      s.tAngle,
      s.prevDeg,
      s.prevDist,
      object.getProperty<number>("angle-threshold") ?? 0.2,
      object.getProperty<number>("distance-threshold") ?? 0.5,
      options.forceImmediate === true ? 1 : 0,
      options.forceEmit === true ? 1 : 0,
      options.bypassDeadzone === true ? 1 : 0,
      this.stepResult
    );

    s.angle = this.stepResult.angle;
    s.dist = this.stepResult.dist;
    s.tAngle = this.stepResult.tAngle;
    s.tDist = this.stepResult.tDist;

    object.setProperty("spotlight-distance-target", s.tDist);
    object.setProperty("spotlight-angle-rad-target", s.tAngle);
    object.setProperty("spotlight-angle-rad", s.angle);
    object.setProperty("spotlight-distance", s.dist);

    if (this.stepResult.angleChanged === 1 || this.stepResult.distChanged === 1) {
      this.writeSpotlightVars(object, this.stepResult.cssDeg, this.stepResult.cssDist);

      s.prevDeg = this.stepResult.degRaw;
      s.prevDist = s.dist;

      this.events.emit(this.getObjectEventName(object, "spotlight:update"), {
        distance: this.stepResult.cssDist,
        angleDeg: this.stepResult.cssDeg,
      });
    }
  }

  private writeSpotlightVars(object: StringObject, angleDeg: number, distance: number): void {
    const apply = () => {
      this.applyToElementAndConnects(object, (el) => {
        styleTxn.setVar(el, "--spotlight-angle", angleDeg);
        styleTxn.setVar(el, "--spotlight-distance", distance);
      });
    };

    if (styleTxn.isOpen) {
      apply();
      return;
    }

    styleTxn.run(apply);
  }
}
